/**
 * Engine E3.3e — client helper for the plan-EDIT flow (plan-page + diff card).
 *
 * The athlete describes a change in plain language on a week page. We run the
 * two-turn backend contract:
 *   1. POST /api/plans/patch-intent  — INTERPRET only. Returns the resolved,
 *      trusted PlanPatchIntent + (for a destructive edit) a deterministic
 *      confirmation echo. Persists NOTHING.
 *   2. POST /api/plans/patch-apply   — APPLY a confirmed intent (the write).
 *      Destructive edits require `confirmed: true`; the response carries a
 *      human disclosure of exactly what changed (the diff card).
 * Plus POST /api/plans/patch-rollback to restore a prior version (Option A).
 *
 * Pure-ish: the three request fns are thin authed POSTs (→ null on a
 * network/parse error so the UI degrades to an error state); the classifiers +
 * the result summarizer are pure and unit-tested. Mirrors engineDraft.ts.
 */

export interface PlanPatchIntent {
  targetWeeks: number[];
  targetDays: string[];
  operation: string;
  userInstruction: string;
  allowLocalRebalance: boolean;
  allowArcRebalance: boolean;
  /**
   * Set by the server normalize (api/lib/planning/planPatchIntent.js): an
   * EXPLICIT whole-week scope (targetDays empty on purpose). TYPE-ONLY
   * addition here (B42-A F3 reads it for the durable-preference mapping);
   * the field was always present on the wire.
   */
  wholeWeek?: boolean;
  /**
   * B42-C — the athlete's literal target pace ("8:15") when an adjust_pace
   * intent resolved one from their words. Absent otherwise. TYPE-ONLY
   * addition (B42-A F3 reads it for the pace_overrides mapping).
   */
  targetPace?: string;
}

/** /api/plans/patch-intent response. */
export interface PatchIntentResult {
  ok: boolean;
  // ready | needs_confirmation | extract_failed | invalid_intent | empty_message
  // | disabled | no_season
  status: string;
  intent: PlanPatchIntent | null;
  requiresConfirmation: boolean;
  confirmationEcho: string | null;
  risk?: string | null;
  reasons?: string[];
  message?: string;
  [k: string]: unknown;
}

export interface PatchAppliedWeek {
  weekNumber: number;
  ok: boolean;
  changedDays?: string[];
  localChanges?: string[];
}

// EB — edit pushback. One reason the edit degrades the week (evidence-cited,
// plain language). `blocking` findings demand an explicit resolution; a
// non-blocking one (a clinical guard that held the request) is informational.
export interface PushbackFinding {
  code: string;
  weekNumber: number;
  blocking: boolean;
  message: string;
  before?: { value: number | null; limit: number | null };
  after?: { value: number | null; limit: number | null };
  citation?: string | null;
}

/** The coach's balanced counter-offer (full plans live server-side). */
export interface PushbackCounter {
  narration: string;
  disclosure: string[];
  weeks: { weekNumber: number; changedDays: string[] }[];
}

/** The deterministic pushback verdict carried on a preview / apply response. */
export interface PushbackPayload {
  findings: PushbackFinding[];
  counter: PushbackCounter | null;
  overrideAllowed: boolean;
  refusal: { message: string } | null;
}

/** An explicit resolution of a degrading edit. */
export type EditResolution = 'override' | 'counter';

/** /api/plans/patch-apply response. */
export interface PatchApplyResult {
  ok: boolean;
  // preview (dry-run) | applied | partial | needs_confirmation | needs_review
  // | needs_resolution | refused | invalid_intent | no_season | disabled
  // | extract_failed
  status: string;
  applied?: PatchAppliedWeek[];
  disclosure?: string[];
  /** Coach's plain-language summary of the edit (guarded server-side). */
  narration?: string;
  confirmationEcho?: string | null;
  multiWeekWarnings?: unknown[];
  unversionedWeeks?: number[];
  /** EB — the pushback verdict (present on a preview / needs_resolution / refused). */
  pushback?: PushbackPayload;
  /** EB — which resolution the applied write used ('override' | 'counter'). */
  appliedResolution?: EditResolution;
  message?: string;
  [k: string]: unknown;
}

/** /api/plans/patch-rollback response. */
export interface RollbackResult {
  ok: boolean;
  // rolled_back | nothing_to_rollback | already_active | no_history
  // | needs_review | no_season | disabled
  status: string;
  weekNumber?: number;
  restoredFromVersion?: number;
  newVersionNumber?: number;
  message?: string;
  [k: string]: unknown;
}

type AuthFetch = (url: string, init?: { method?: string; body?: string }) => Promise<Response>;

/**
 * INTERPRET an edit request. Returns null on a network/parse error.
 *
 * `contextWeek` is the week page the athlete is editing FROM. The backend uses
 * it as the default scope anchor when the message names no explicit week (e.g.
 * "make Tuesday harder" on the Week 5 page targets Week 5, not the season's
 * calendar week). Explicit week numbers in the message still override it; the
 * confirmation echo + apply-time validation still bound the resolved scope.
 */
export async function requestPatchIntent(
  fetchAuthed: AuthFetch,
  message: string,
  contextWeek?: number,
  /**
   * #3 per-day entry — the weekday the athlete is editing (e.g. "Wed"). Lets a
   * deictic change ("make this easier") scope to THAT day without naming it.
   * The backend only uses it when the message names no day and isn't a
   * whole-week request; a named day / "whole week" still win.
   */
  contextDay?: string,
): Promise<PatchIntentResult | null> {
  try {
    const res = await fetchAuthed('/api/plans/patch-intent', {
      method: 'POST',
      body: JSON.stringify({
        message,
        ...(typeof contextWeek === 'number' && Number.isInteger(contextWeek) && contextWeek >= 1
          ? { contextWeek }
          : {}),
        ...(typeof contextDay === 'string' && contextDay ? { contextDay } : {}),
      }),
    });
    return (await res.json()) as PatchIntentResult;
  } catch {
    return null;
  }
}

/**
 * DRY-RUN preview: compute the real before/after for an intent WITHOUT persisting
 * (the backend runs patch → rebalance → validate → diff → narration and writes
 * nothing). Returns a result with `status: 'preview'`, or null on a network/parse
 * error. The athlete reviews this, then calls applyPatch to commit.
 */
export async function previewPatch(
  fetchAuthed: AuthFetch,
  intent: PlanPatchIntent,
): Promise<PatchApplyResult | null> {
  try {
    const res = await fetchAuthed('/api/plans/patch-apply', {
      method: 'POST',
      body: JSON.stringify({ intent, dryRun: true }),
    });
    return (await res.json()) as PatchApplyResult;
  } catch {
    return null;
  }
}

/**
 * APPLY a confirmed intent. `confirmed` MUST be true for a destructive edit (the
 * backend echoes instead of applying otherwise). `resolution` is required when
 * the edit DEGRADES the week (EB): 'counter' applies the balanced version,
 * 'override' applies it as asked against advice. The server recomputes the
 * verdict and never trusts the client echo. Returns null on a network/parse error.
 *
 * B515 — `idempotencyKey` is the plan edit card's retry key: the server runs an apply that carries one
 * once, and a repeat of the same key replays the stored answer instead of applying the edit again. One
 * key per tap; a "Try again" after a lost answer resends the same key. Absent, the body is what it was.
 */
export async function applyPatch(
  fetchAuthed: AuthFetch,
  intent: PlanPatchIntent,
  confirmed: boolean,
  resolution?: EditResolution,
  idempotencyKey?: string,
): Promise<PatchApplyResult | null> {
  try {
    const res = await fetchAuthed('/api/plans/patch-apply', {
      method: 'POST',
      body: JSON.stringify({
        intent,
        confirmed,
        ...(resolution ? { resolution } : {}),
        ...(idempotencyKey ? { idempotencyKey } : {}),
      }),
    });
    return (await res.json()) as PatchApplyResult;
  } catch {
    return null;
  }
}

/** ROLL BACK a week to a prior version (default: undo the last change). */
export async function rollbackWeek(
  fetchAuthed: AuthFetch,
  weekNumber: number,
  toVersionNumber?: number,
): Promise<RollbackResult | null> {
  try {
    const res = await fetchAuthed('/api/plans/patch-rollback', {
      method: 'POST',
      body: JSON.stringify({ weekNumber, ...(toVersionNumber != null ? { toVersionNumber } : {}) }),
    });
    return (await res.json()) as RollbackResult;
  } catch {
    return null;
  }
}

// ── pure classifiers (unit-tested) ───────────────────────────────────────────

/** A usable, low-risk intent — safe to apply directly (no confirm needed). */
export function intentReady(r: PatchIntentResult | null): boolean {
  return !!r && r.ok && r.status === 'ready' && !r.requiresConfirmation && !!r.intent;
}

/** A usable but DESTRUCTIVE intent — show the echo + require an explicit confirm. */
export function intentNeedsConfirmation(r: PatchIntentResult | null): boolean {
  return !!r && r.ok && r.status === 'needs_confirmation' && r.requiresConfirmation && !!r.intent;
}

/**
 * The interpret turn did not yield a usable intent (couldn't understand it, no
 * season, engine disabled, or a network/parse error → null). The UI shows a
 * friendly message; never a raw failure.
 */
export function intentFailed(r: PatchIntentResult | null): boolean {
  return !intentReady(r) && !intentNeedsConfirmation(r);
}

/**
 * ⚖️ Critic-4 #3 — the COPPA hold, on ANY status. `coppaHoldResponse`
 * (api/index.js) answers 403 with `{ error, code: 'guardian_consent_required' }`
 * and no `message`, so both failure-message helpers fell through to the generic
 * line: a held under-13 was told to rephrase, forever, whatever they typed. The
 * hold is the CODE, never the status — the same body arrives from the interpret
 * turn, the dry run and the apply. Rephrasing cannot clear it, so the panel
 * renders it as a hold rather than an error.
 *
 * ⚖️ B198 fix 2 (harvest security F1) — "Settings, under Guardians" named a
 * panel a guardian-only parent (the common case) does not have: `Under13ConsentRow`
 * mounts only inside `athleteSections`, never `guardianSections`
 * (`routes/settings.tsx:329`, `:249-252`). A held minor and her parent who
 * followed the one instruction the product gave dead-ended. "Their own Tayro
 * account" names no panel, so it is true for both the guardian-only path
 * (lane b201, the guardian home) and the dual-account path (Settings).
 */
export const GUARDIAN_HOLD_MESSAGE =
  'Your coach stays paused until a parent or guardian approves this account. ' +
  'They can do it from their own Tayro account.';

/** True when the response is the guardian hold (the account is paused, not the edit). */
export function editGuardianHold(r: { code?: unknown; [k: string]: unknown } | null): boolean {
  return !!r && r.code === 'guardian_consent_required';
}

/**
 * B196 L5 (spec §4.1, §4.2, §4.8) — the floor and the unknown-age wall follow
 * the SAME house pattern as `GUARDIAN_HOLD_MESSAGE` + `editGuardianHold`
 * above. Two new codes, pinned to the spec's own literal spellings:
 *
 *   - `age_below_minimum` (RULE 1, the floor) — an existing account's write
 *     that would establish, preserve, or erase-into-unknown an under-13 age.
 *     The signup-time variant of this code (the stop screen) is L2's
 *     `AgeStep.tsx`, not this surface; the sentence below is the "existing
 *     account" line the spec's §4.8 table gives it.
 *   - `age_band_required` (RULE 2, the wall) — no age band established on
 *     the request path; a different code, sentence and route from the
 *     guardian hold.
 *
 * A third code, `age_review_pending` (§4.5, the actual-knowledge hold), rides
 * the SAME house pattern below (L9, `lane-b196-l9-client-fixes.md`) — L6's
 * server registry entry (`api/lib/planning/coppaGate.js`'s `HOLD_SENTENCES`)
 * carries it ahead of any decider emitting it, so this predicate is wired
 * against a real literal, not an invented one, even before L6's enforcement
 * half lands.
 *
 * Server side: `api/lib/planning/coppaGate.js` / `api/index.js` (lane L4,
 * built in a parallel worktree). SEAM: `lib/planEdit.codes.b196.test.ts`
 * pins these three literals against the spec text now, and against L4's real
 * source once it lands.
 */
export const AGE_BELOW_MINIMUM_MESSAGE =
  'Tayro is for athletes 13 and up. This was not saved. The account is removed by a parent ' +
  'or guardian where one is linked, and by us where one is not.';

/** True when the response is the RULE 1 floor refusal (an under-13 write). */
export function editAgeBelowMinimum(r: { code?: unknown; [k: string]: unknown } | null): boolean {
  return !!r && r.code === 'age_below_minimum';
}

export const AGE_BAND_REQUIRED_MESSAGE =
  'We need your birth month and year before coaching can run. You can add it in setup.';

/** True when the response is the RULE 2 wall refusal (no established age band). */
export function editAgeBandRequired(r: { code?: unknown; [k: string]: unknown } | null): boolean {
  return !!r && r.code === 'age_band_required';
}

/**
 * B196 L9 (spec §4.5, §4.8; UX review finding 4) — RULE 2.6a, the
 * actual-knowledge review hold: L6's `decideAgeReviewHold` stamps
 * `age_review_state = 'held'` and the SAME `checkCoppaGate` family refuses
 * the protected set with this code. Byte-identical to
 * `coppaGate.js`'s `HOLD_SENTENCES[AGE_REVIEW_PENDING_CODE]` — see the seam
 * test. `JoinTeamModal.tsx` pinned this sentence locally before this lane
 * exported it (its own comment says why); it is left as-is, not migrated to
 * this constant, per this lane's OWNS.
 */
export const AGE_REVIEW_PENDING_MESSAGE =
  'We are checking something about this account. Coaching is paused while we check.';

/** True when the response is the actual-knowledge review hold. */
export function editAgeReviewPending(r: { code?: unknown; [k: string]: unknown } | null): boolean {
  return !!r && r.code === 'age_review_pending';
}

/**
 * Decision 3 (founder, 2026-09-29): with no season to generate into, the Plan
 * tab reads this one sentence, whole, with no "Couldn't generate plan: "
 * prefix. `useGenerateMacroPlan` throws it with the code; `EmptyMacroPlan`
 * keys on the code, never on the words.
 */
export const NO_ACTIVE_SEASON = 'no_active_season';
export const NO_ACTIVE_SEASON_MESSAGE = 'Couldn’t generate a plan. Start a season first.';

/** A human, athlete-safe message for a failed/again-needed interpret turn. */
export function intentFailureMessage(r: PatchIntentResult | null): string {
  if (editGuardianHold(r)) return GUARDIAN_HOLD_MESSAGE;
  if (r && typeof r.message === 'string' && r.message.trim()) return r.message.trim();
  if (r && r.status === 'disabled') return 'Plan editing isn’t available right now.';
  if (r && r.status === 'no_season') return 'There’s no active plan to edit yet.';
  return 'I couldn’t quite understand that change. Try rephrasing (e.g. “make Wednesday easier”).';
}

/** The dry-run produced a usable before/after preview (nothing persisted yet). */
export function patchPreviewReady(r: PatchApplyResult | null): boolean {
  return !!r && r.ok && r.status === 'preview';
}

/** The apply turn persisted the edit cleanly. */
export function patchApplied(r: PatchApplyResult | null): boolean {
  return !!r && r.ok && r.status === 'applied';
}

/**
 * Diff-card lines for an apply result: the backend disclosure when present
 * (e.g. "Week 5: Thu changed (Easy Run → Tempo Run)"), else a terse per-week
 * fallback derived from the changed days. Always returns at least one line on a
 * successful apply.
 */
export function summarizePatchResult(r: PatchApplyResult | null): string[] {
  if (!r) return [];
  if (Array.isArray(r.disclosure) && r.disclosure.length > 0) {
    return r.disclosure.filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
  }
  const lines: string[] = [];
  for (const w of Array.isArray(r.applied) ? r.applied : []) {
    if (w && w.ok && Array.isArray(w.changedDays) && w.changedDays.length > 0) {
      lines.push(`Week ${w.weekNumber}: updated ${w.changedDays.join(', ')}`);
    }
  }
  return lines;
}

/**
 * The coach's plain-language summary of the applied edit (guarded server-side),
 * shown above the precise diff lines. Null when absent (older backend) so the
 * card degrades to its generic header.
 */
export function patchNarration(r: PatchApplyResult | null): string | null {
  if (!r) return null;
  return typeof r.narration === 'string' && r.narration.trim().length > 0 ? r.narration.trim() : null;
}

/** A human, athlete-safe message for a non-applied apply result. */
export function patchFailureMessage(r: PatchApplyResult | null): string {
  if (editGuardianHold(r)) return GUARDIAN_HOLD_MESSAGE;
  if (r && typeof r.message === 'string' && r.message.trim()) return r.message.trim();
  if (r && r.status === 'needs_review') return 'I couldn’t apply that change, so I left your plan as-is.';
  if (r && r.status === 'no_season') return 'There’s no active plan to edit yet.';
  return 'That change couldn’t be applied. Your plan is unchanged.';
}

// ── EB — edit pushback ───────────────────────────────────────────────────────

/**
 * The pushback verdict on a preview / apply result, or null when the edit
 * doesn't degrade the week. Present iff the server attached at least one finding.
 */
export function patchPushback(r: PatchApplyResult | null): PushbackPayload | null {
  if (!r || !r.pushback || typeof r.pushback !== 'object') return null;
  const pb = r.pushback;
  return Array.isArray(pb.findings) && pb.findings.length > 0 ? pb : null;
}

/** True when the pushback carries a BLOCKING finding — an explicit resolution is required. */
export function pushbackBlocking(pb: PushbackPayload | null): boolean {
  return !!pb && pb.findings.some((f) => f.blocking);
}

/** The apply turn needs an explicit resolution (the degrading edit wasn't resolved). */
export function patchNeedsResolution(r: PatchApplyResult | null): boolean {
  return !!r && r.status === 'needs_resolution';
}

/** The edit was refused outright (youth duty-of-care — the engine floor wins). */
export function patchRefused(r: PatchApplyResult | null): boolean {
  return !!r && r.status === 'refused';
}

// ── B515 / B516 — the plan edit card (chat) ──────────────────────────────────
//
// The server's `plan_edit` frame (`buildPlanEditEvent`, api/index.js) and the pure half of the card that draws
// it: which card a frame is, the sentence it reads, what each answer of `patch-apply` means, and the retry
// key. `components/chat/PlanEditCard.tsx` draws and holds the state; everything here is pure and unit-tested,
// and `tests/b515-card-client-contract.test.js` runs it over a REAL frame from the real route.

/**
 * B42-C / B515 / B516 — the outcome of a chat-originated plan edit for ONE turn, as the server writes it.
 *
 * `saved` is the only trustworthy claim about the write: the coach's prose was generated BEFORE the edit ran
 * and is instructed never to say a change is saved, so this frame (not the assistant text) is what a surface
 * may believe. `status` mirrors the patch-apply contract: 'applied' | 'partial' | 'needs_confirmation' |
 * 'needs_resolution' | 'needs_review' | 'refused' | ….
 *
 * `intent` is on a `needs_confirmation` echo, and on the one-coach lane's `needs_resolution` of a low risk edit
 * (with its `confirmationEcho`, B516 follow-up): the change itself, which the card sends back to
 * `POST /api/plans/patch-apply` with `confirmed: true` (the server checks it again there). `speaker` is the
 * room's: the id of the coach that spoke the frame (the one-coach chat's frame has none). `disclosure` is the
 * list of lines the diff builder wrote (`summarizeDiff`), not one string.
 */
export interface PlanEditFrame {
  type: 'plan_edit';
  status: string;
  saved: boolean;
  weekNumber: number | null;
  changedDays: string[];
  narration?: string;
  disclosure?: string[];
  confirmationEcho?: string;
  message?: string;
  intent?: PlanPatchIntent;
  speaker?: string;
}

/**
 * B516 — the message a plan edit frame belongs to in a one-coach thread: the last reply (not a system note) after the
 * athlete's last message, or -1 when that turn has no reply with words in it. The frame follows the reply it is about,
 * so a reply of an earlier turn is never the answer.
 */
export function lastReplyOfTurn(
  messages: ReadonlyArray<{ role: string; content: string; system?: boolean }>,
): number {
  let floor = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]!.role === 'user' && messages[i]!.system !== true) {
      floor = i;
      break;
    }
  }
  for (let i = messages.length - 1; i > floor; i--) {
    const m = messages[i]!;
    if (m.role === 'assistant' && m.system !== true && m.content.trim().length > 0) return i;
  }
  return -1;
}

/** The exact tail of the server's echo ("... in Week 4 only, confirm?") the card replaces with a period. */
const CONFIRM_SUFFIX = ', confirm?';

/**
 * The sentence the card shows (founder 2026-10-29, DECISIONS row 1): the server's `confirmationEcho` with
 * ONLY an exact trailing ", confirm?" replaced by a period. The button is the question now. An echo that
 * does not end that way is shown unchanged, so the scope the athlete checks is never rewritten.
 */
export function cardSentence(echo: string): string {
  return echo.endsWith(CONFIRM_SUFFIX) ? `${echo.slice(0, -CONFIRM_SUFFIX.length)}.` : echo;
}

/** "Week 4", "Weeks 4 and 5", "Weeks 4, 5, and 6": the server's own list form (`joinWeeks`, planPatchIntent.js). */
export function weeksLabel(weeks: readonly number[]): string {
  if (weeks.length === 0) return '';
  if (weeks.length === 1) return `Week ${weeks[0]}`;
  if (weeks.length === 2) return `Weeks ${weeks[0]} and ${weeks[1]}`;
  return `Weeks ${weeks.slice(0, -1).join(', ')}, and ${weeks[weeks.length - 1]}`;
}

/**
 * A fresh retry key for one Apply tap: 16 to 128 characters of [A-Za-z0-9_-], which is what the server's
 * `planPatchRetryKey` accepts (anything else is simply not a key, and the apply then runs as it always did).
 */
export function newApplyKey(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return `edit-${c.randomUUID()}`;
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return `edit-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

/** Which card a `plan_edit` frame is. `proposal` is the only one with something to apply. */
export interface PlanEditCardSeed {
  source: 'proposal' | 'receipt' | 'refusal';
  /** The weeks the card names, in the server's order (empty when the frame names none). */
  weeks: number[];
  /** proposal: the sentence, ", confirm?" dropped. */
  sentence: string;
  /** proposal: the change itself, sent back on Apply. */
  intent: PlanPatchIntent | null;
  /** receipt: what changed. */
  lines: string[];
  /** receipt: Airo's one-paragraph summary, when the server wrote one. */
  narration: string | null;
  /** refusal: the server's sentence, verbatim. */
  note: string;
}

function isPlanPatchIntent(x: unknown): x is PlanPatchIntent {
  if (!x || typeof x !== 'object') return false;
  const i = x as { targetWeeks?: unknown; targetDays?: unknown; operation?: unknown };
  return Array.isArray(i.targetWeeks) && Array.isArray(i.targetDays) && typeof i.operation === 'string';
}

const validWeek = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 1;

/**
 * The card a frame draws, or null when the frame is not for a card (an older server, a status with nothing to
 * offer or to say). Mounted by `speaker` in the room, by the lane in the one-coach chat; never decided here.
 *
 *   - `needs_confirmation` with an intent and an echo: the PROPOSAL. Nothing is written until the athlete taps.
 *   - `needs_resolution` with an intent and an echo (B516 follow-up: a low risk edit in the one-coach chat whose apply
 *     degrades the week and waits on the athlete): the same PROPOSAL. Its dry run on arrival returns the pushback, so the
 *     card opens on "Needs your call" before any tap. Without the intent and the echo (an older server) it is no card.
 *   - `applied` with `saved: true`: the RECEIPT (a low risk edit in the one-coach chat arrives already applied).
 *   - any other status that carries a `message` and no intent: a REFUSAL, the server's sentence, no buttons.
 *   - everything else (partial, needs_resolution with no message, no echo): nothing. `saved` is the only
 *     honest claim, so a frame that did not save a week never draws a receipt.
 */
export function cardFromFrame(frame: PlanEditFrame): PlanEditCardSeed | null {
  const frameWeek = validWeek(frame.weekNumber) ? [frame.weekNumber] : [];
  if (
    (frame.status === 'needs_confirmation' || frame.status === 'needs_resolution') &&
    isPlanPatchIntent(frame.intent) &&
    typeof frame.confirmationEcho === 'string' &&
    frame.confirmationEcho.trim().length > 0
  ) {
    const fromIntent = frame.intent.targetWeeks.filter(validWeek);
    return {
      source: 'proposal',
      weeks: fromIntent.length > 0 ? fromIntent : frameWeek,
      sentence: cardSentence(frame.confirmationEcho),
      intent: frame.intent,
      lines: [],
      narration: null,
      note: '',
    };
  }
  if (frame.status === 'applied' && frame.saved === true) {
    const given = Array.isArray(frame.disclosure)
      ? frame.disclosure
      : typeof frame.disclosure === 'string'
        ? [frame.disclosure]
        : [];
    const fromDisclosure = given.filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
    const days = Array.isArray(frame.changedDays) ? frame.changedDays.filter((d) => typeof d === 'string' && d) : [];
    const lines =
      fromDisclosure.length > 0
        ? fromDisclosure
        : frameWeek.length > 0 && days.length > 0
          ? [`Week ${frameWeek[0]}: updated ${days.join(', ')}`]
          : [];
    return {
      source: 'receipt',
      weeks: frameWeek,
      sentence: '',
      intent: null,
      lines,
      narration: typeof frame.narration === 'string' && frame.narration.trim() ? frame.narration.trim() : null,
      note: '',
    };
  }
  if (!isPlanPatchIntent(frame.intent) && typeof frame.message === 'string' && frame.message.trim()) {
    return {
      source: 'refusal',
      weeks: frameWeek,
      sentence: '',
      intent: null,
      lines: [],
      narration: null,
      note: frame.message.trim(),
    };
  }
  return null;
}

/**
 * What the dry run said. A failed preview (no answer, an error body) is not an error line: the lines are just gone. A
 * hold or a refusal is the server's sentence in place of the buttons.
 */
export type CardPreviewOutcome =
  | { kind: 'ready'; lines: string[]; pushback: PushbackPayload | null }
  | { kind: 'note'; message: string }
  | { kind: 'failed' };

/** The sentence a hold (guardian, age floor, age wall, review hold) puts in place of the buttons, or null. */
function holdSentence(r: { code?: unknown; [k: string]: unknown } | null): string | null {
  if (editGuardianHold(r)) return GUARDIAN_HOLD_MESSAGE;
  if (editAgeBelowMinimum(r)) return AGE_BELOW_MINIMUM_MESSAGE;
  if (editAgeBandRequired(r)) return AGE_BAND_REQUIRED_MESSAGE;
  if (editAgeReviewPending(r)) return AGE_REVIEW_PENDING_MESSAGE;
  return null;
}

export function classifyCardPreview(r: PatchApplyResult | null): CardPreviewOutcome {
  const hold = holdSentence(r);
  if (hold) return { kind: 'note', message: hold };
  if (patchPreviewReady(r)) return { kind: 'ready', lines: summarizePatchResult(r), pushback: patchPushback(r) };
  // The server REFUSED the change on the dry run (needs_review: it cannot apply it; refused): it will refuse the
  // apply too, so the card says so now, in the server's own sentence, with no button to tap. Like a hold.
  if (r && (r.status === 'needs_review' || r.status === 'refused')) return { kind: 'note', message: patchFailureMessage(r) };
  return { kind: 'failed' };
}

/** What an Apply, Keep it balanced or Apply as asked tap got back. */
export type CardApplyOutcome =
  | { kind: 'applied'; lines: string[]; narration: string | null; resolution: EditResolution | null }
  | { kind: 'pushback'; pushback: PushbackPayload }
  | { kind: 'note'; message: string }
  | { kind: 'network' };

/**
 * The answer of `patch-apply` to the card's tap, as the card draws it.
 *
 *   - no answer: null (the request failed), the 409 `in_progress` the retry key answers while the first run is
 *     still writing, and any body with no status (a 5xx, a 4xx the card cannot name): NETWORK. The line does not
 *     claim nothing changed, because the write may have landed; Try again resends the same key.
 *   - applied, or partial with at least one week saved: the RECEIPT.
 *   - needs_resolution: the pushback (the verdict shifted since the preview), so the athlete decides again.
 *   - a hold code, a refusal, needs_review and every other status: a NOTE, the server's sentence in place of the
 *     buttons.
 */
export function classifyCardApply(r: PatchApplyResult | null): CardApplyOutcome {
  const hold = holdSentence(r);
  if (hold) return { kind: 'note', message: hold };
  if (!r || typeof r.status !== 'string' || r.status === 'in_progress') return { kind: 'network' };
  const savedAWeek = Array.isArray(r.applied) && r.applied.some((w) => !!w && w.ok === true);
  if (patchApplied(r) || (r.status === 'partial' && savedAWeek)) {
    const resolution = r.appliedResolution === 'counter' || r.appliedResolution === 'override' ? r.appliedResolution : null;
    return { kind: 'applied', lines: summarizePatchResult(r), narration: patchNarration(r), resolution };
  }
  if (patchNeedsResolution(r)) {
    const pushback = patchPushback(r);
    if (pushback) return { kind: 'pushback', pushback };
  }
  return { kind: 'note', message: patchFailureMessage(r) };
}
